import {
  Box,
  Container,
  HStack,
  Text,
  Link,
  Button,
  useToast,
} from "@chakra-ui/react"
import { Link as RouterLink } from "react-router-dom"
import { useAuth } from "../../../contexts/AuthContext";
import { getColor, brandConfig, getComponent } from "../../../brandConfig";
import { resetFloatingPanels } from "../../../hooks/floatingPanels";

export const FooterWithFourColumns = () => {
  const { isAuthenticated } = useAuth();
  const toast = useToast();
  // "↻ Reset floating panels" — the one common reset (vault/how-to/draggable-floating-launchers.md).
  const onResetPanels = () => {
    resetFloatingPanels();
    toast({ title: "Panels reset", description: "Every floating button and menu is back in its home position and showing again.", status: "success", duration: 3000, isClosable: true });
  };

  return (
    <Box
      bg={getComponent("footer", "bg")}
      borderTop="1px solid"
      borderColor={getColor("border.light")}
      py={6}
    >
      <Container as="footer" role="contentinfo" maxW="1200px">
        {/* Navigation links - Row 1: Main pages */}
        <HStack
          justify="center"
          spacing={{ base: 4, md: 8 }}
          flexWrap="wrap"
          mb={3}
        >
          {/* Sam Manger, website feedback 27 Jul 2026: Benefits, FAQ (now on the homepage), For providers
              (still in the top bar), Updates and Cheatsheets come off the footer. */}
          {[
            { label: "Home", to: "/" },
            { label: "About", to: "/about" },
            { label: "How It Works", to: "/how-it-works" },
            { label: "Pricing", to: "/pricing" },
            { label: "Contact", to: "/contact" },
          ].map((link) => (
            <Link
              key={link.to}
              as={RouterLink}
              to={link.to}
              color={getComponent("footer", "linkColor")}
              fontSize="sm"
              fontWeight="400"
              transition="all 0.2s ease"
              _hover={{
                color: getComponent("footer", "linkHover"),
                textDecoration: "none"
              }}
            >
              {link.label}
            </Link>
          ))}
        </HStack>

        {/* Navigation links - Row 2: Legal + member pages */}
        <HStack
          justify="center"
          spacing={{ base: 4, md: 8 }}
          flexWrap="wrap"
          mb={6}
        >
          {[
            { label: "Terms", to: "/terms" },
            { label: "Privacy", to: "/privacy" },
            ...(!isAuthenticated ? [
              { label: "Log In", to: "/login" },
            ] : []),
          ].map((link) => (
            <Link
              key={link.to}
              as={RouterLink}
              to={link.to}
              color={getComponent("footer", "linkColor")}
              fontSize="sm"
              fontWeight="400"
              transition="all 0.2s ease"
              _hover={{
                color: getComponent("footer", "linkHover"),
                textDecoration: "none"
              }}
            >
              {link.label}
            </Link>
          ))}
        </HStack>

        {isAuthenticated && (
          <Box textAlign="center" mb={4}>
            <Button
              size="sm"
              minH="44px"
              variant="ghost"
              fontSize="sm"
              color={getComponent("footer", "linkColor")}
              onClick={onResetPanels}
              title="Bring every floating button and menu back to its home position and show any you hid"
            >
              ↻ Reset floating panels
            </Button>
          </Box>
        )}

        {/* Copyright text */}
        <Text fontSize="xs" color={getComponent("footer", "text")} textAlign="center">
          &copy; {new Date().getFullYear()} {brandConfig.siteName}. All rights reserved.
        </Text>
      </Container>
    </Box>
  )
}
